/* ============================================================
 * HUB Workwell · Dashboard de fisioterapia (página autónoma)
 * Substitui a versão anterior deste ficheiro.
 *
 * /fisio/<token> — o cliente escreve o email, recebe um código
 * de 6 dígitos e fica com sessão de 7 dias. Só emails
 * autorizados entram.
 *
 * Os gráficos vivem em src/components/FisioGraficos.jsx, para
 * serem os mesmos aqui e na secção do portal.
 *
 * Guardar em src/pages/DashboardFisio.jsx
 * ============================================================ */

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';           // <-- ajustar se o caminho for outro
import FisioGraficos from '../components/FisioGraficos';

const ROXO = '#691FB1';
const ROXO_ESC = '#4A1580';
const ROXO_MED = '#736CCE';
const ROSA = '#FF357B';
const ROSA_ESC = '#BE1356';
const TINTA = '#1E1A2B', TINTA2 = '#55506B', TINTA3 = '#8A85A0';
const LINHA = '#E7E4EE';

export default function DashboardFisio() {
  // o App.js encaminha pelo endereço, sem react-router
  const token = (window.location.pathname.split('/fisio/')[1] || '').split('/')[0];
  const chaveSessao = `fisio_sessao_${token}`;

  const [sessao, setSessao] = useState(() => {
    try { return localStorage.getItem(chaveSessao) || ''; } catch { return ''; }
  });
  const [autenticado, setAutenticado] = useState(false);
  const [fase, setFase] = useState('email');
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [dados, setDados] = useState(null);
  const [aCarregar, setACarregar] = useState(false);
  const [filtros, setFiltros] = useState({ ano: '', mes: '', local: '' });

  const esquecerSessao = () => {
    try { localStorage.removeItem(chaveSessao); } catch { /* ignorar */ }
    setSessao(''); setAutenticado(false); setDados(null); setFase('email'); setCodigo('');
  };

  const carregar = useCallback(async (ses, f) => {
    setACarregar(true); setErro('');
    const { data, error } = await supabase.rpc('fisio_dashboard_dados', {
      p_token: token,
      p_sessao: ses,
      p_ano: f.ano ? Number(f.ano) : null,
      p_mes: f.mes ? Number(f.mes) : null,
      p_local: f.local || null,
    });
    setACarregar(false);
    if (error) {
      try { localStorage.removeItem(chaveSessao); } catch { /* ignorar */ }
      setSessao(''); setAutenticado(false); setFase('email'); setCodigo('');
      setErro(/expirada|inv|retirado/i.test(error.message)
        ? 'A sessão terminou. Peça um código novo.' : error.message);
      return false;
    }
    try { localStorage.setItem(chaveSessao, ses); } catch { /* ignorar */ }
    setAutenticado(true); setDados(data);
    return true;
  }, [token, chaveSessao]);

  const pedirCodigo = async e => {
    e.preventDefault();
    setACarregar(true); setErro(''); setAviso('');
    const { data, error } = await supabase.functions.invoke('fisio-codigo', {
      body: { token, email: email.trim() },
    });
    setACarregar(false);
    if (error || data?.erro) {
      setErro(data?.erro || 'Não foi possível enviar o código. Tente de novo.');
      return;
    }
    setFase('codigo');
    setAviso('Se o email estiver autorizado, o código chega dentro de momentos.');
  };

  const validarCodigo = async e => {
    e.preventDefault();
    setACarregar(true); setErro('');
    const { data, error } = await supabase.rpc('fisio_codigo_validar', {
      p_token: token, p_email: email.trim(), p_codigo: codigo.trim(),
    });
    setACarregar(false);
    if (error || !data || !data.length) { setErro('Código inválido ou expirado.'); return; }
    const ses = data[0].sessao;
    setSessao(ses);
    carregar(ses, filtros);
  };

  useEffect(() => { if (sessao) carregar(sessao, filtros); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (autenticado) carregar(sessao, filtros); /* eslint-disable-next-line */ }, [filtros]);

  /* --- entrada: email, depois código --- */
  if (!autenticado) {
    const campo = {
      width: '100%', padding: '10px 12px', border: `1px solid ${LINHA}`,
      borderRadius: 9, fontSize: 14, marginBottom: 12, boxSizing: 'border-box',
    };
    const botao = {
      width: '100%', padding: '10px 12px', background: ROXO, color: '#fff', border: 'none',
      borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer',
    };
    return (
      <div style={{ minHeight: '100vh', background: '#FCFCFB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif' }}>
        <form onSubmit={fase === 'email' ? pedirCodigo : validarCodigo}
              style={{ background: '#fff', border: `1px solid ${LINHA}`, borderRadius: 16, padding: 32, width: 380 }}>
          <img src="/workwell-horizontal.png"
               srcSet="/workwell-horizontal.png 1x, /workwell-horizontal-2x.png 2x"
               alt="Workwell" style={{ height: 34, width: 'auto', display: 'block', marginBottom: 18 }} />
          <h1 style={{ fontSize: 17, margin: '0 0 6px', color: TINTA }}>Dashboard de fisioterapia</h1>

          {fase === 'email' ? (
            <>
              <p style={{ fontSize: 13, color: TINTA2, margin: '0 0 18px' }}>
                Escreva o seu email. Enviamos-lhe um código para entrar.
              </p>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                     placeholder="o.seu.email@empresa.pt" autoFocus required style={campo} />
              {erro && <div style={{ color: ROSA_ESC, fontSize: 12.5, marginBottom: 12 }}>{erro}</div>}
              <button type="submit" disabled={aCarregar} style={botao}>
                {aCarregar ? 'A enviar…' : 'Enviar código'}
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, color: TINTA2, margin: '0 0 18px' }}>
                Introduza o código de 6 dígitos enviado para <b>{email}</b>.
              </p>
              <input type="text" inputMode="numeric" maxLength={6} value={codigo}
                     onChange={e => setCodigo(e.target.value.replace(/\D/g, ''))} autoFocus required
                     style={{ ...campo, letterSpacing: '.35em', fontSize: 22, textAlign: 'center', fontWeight: 700 }} />
              {aviso && !erro && <div style={{ color: TINTA3, fontSize: 12.5, marginBottom: 12 }}>{aviso}</div>}
              {erro && <div style={{ color: ROSA_ESC, fontSize: 12.5, marginBottom: 12 }}>{erro}</div>}
              <button type="submit" disabled={aCarregar || codigo.length < 6} style={botao}>
                {aCarregar ? 'A verificar…' : 'Entrar'}
              </button>
              <button type="button" onClick={() => { setFase('email'); setErro(''); setCodigo(''); }}
                      style={{ ...botao, background: 'none', color: TINTA2, fontWeight: 400, fontSize: 12.5, marginTop: 8 }}>
                Usar outro email
              </button>
            </>
          )}

          <p style={{ fontSize: 11.5, color: TINTA3, margin: '16px 0 0' }}>
            Só emails autorizados pela Workwell conseguem entrar.<br />
            Dúvidas: <a href="mailto:apoio@workwell.pt" style={{ color: ROXO }}>apoio@workwell.pt</a>
          </p>
        </form>
      </div>
    );
  }

  if (!dados) return <div style={{ padding: 40, fontFamily: 'Helvetica,Arial,sans-serif', color: TINTA2 }}>A carregar…</div>;

  return (
    <div style={{ background: '#FCFCFB', minHeight: '100vh', fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif', color: TINTA, fontSize: 14 }}>
      <header style={{ background: `linear-gradient(100deg, ${ROXO_ESC} 0%, ${ROXO} 55%, ${ROXO_MED} 100%)`, color: '#fff' }}>
        <div style={{ maxWidth: 1360, margin: '0 auto', padding: '18px 16px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 'clamp(18px,2.4vw,27px)', fontWeight: 800, letterSpacing: '-.02em', textTransform: 'uppercase', lineHeight: 1.1 }}>
              {dados.cliente}
            </div>
            <div style={{ fontSize: 12, opacity: .82, letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 2 }}>
              Programa de Fisioterapia e Bem-Estar no Trabalho
            </div>
          </div>
          <img src="/workwell-selo-branco.png"
               srcSet="/workwell-selo-branco.png 1x, /workwell-selo-branco-2x.png 2x"
               alt="Workwell" style={{ marginLeft: 'auto', height: 58, width: 'auto', display: 'block' }} />
        </div>
      </header>

      <div style={{ maxWidth: 1360, margin: '0 auto', padding: '0 16px 48px' }}>
        <FisioGraficos dados={dados} filtros={filtros} onFiltros={setFiltros} aCarregar={aCarregar} />

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 11.5, color: TINTA3, marginTop: 16, paddingTop: 12, borderTop: `1px solid ${LINHA}` }}>
          {dados.atualizado_em && <span>Dados actualizados em {new Date(dados.atualizado_em).toLocaleString('pt-PT')}</span>}
          <button onClick={esquecerSessao}
                  style={{ background: 'none', border: 'none', color: TINTA3, fontSize: 11.5, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
            Terminar sessão
          </button>
          <span style={{ marginLeft: 'auto' }}>Workwell · documento confidencial — contém apenas dados agregados</span>
        </div>
      </div>
    </div>
  );
}
